/**
 * Betting WebSocket Service
 * Connects to the /api/betting-stream endpoint for real-time betting data
 * Handles: asset_update (prices + strikes + multipliers), betting_error
 */

import { getWebSocketUrl } from '../utils/backendUrl';

// Base interface for priority messages
interface PriorityMessage {
  sequence?: number;
  priority?: 'CRITICAL' | 'IMPORTANT' | 'REALTIME';
  serverTime?: number;
  messageId?: string;
  requiresAck?: boolean;
}

// Base interface for all messages
interface BaseMessage {
  type: string;
  sequence?: number;
  priority?: 'CRITICAL' | 'IMPORTANT' | 'REALTIME';
  serverTime?: number;
  messageId?: string;
  requiresAck?: boolean;
}

// Strike data structure with stable IDs
interface Strike {
  id: string;          // NEW: Stable strike ID (e.g., 'BTC_HIGHER_3X')
  quoteId: string;     // NEW: Quote ID this strike belongs to
  price: number;
  rawPrice: number;
  distance: number;
  multiplier: number;
  probability: number;
  direction: 'higher' | 'lower'; // NEW: Strike direction
}

// Asset data structure with quote information
export interface Asset {
  address: string;
  symbol: string;
  name: string;
  currentPrice: number;
  confidence: number;
  timestamp: number;
  priceHistory?: Array<{
    timestamp: number;
    price: number;
  }>;
  
  // NEW: Quote information
  quoteId?: string;    // Current quote ID
  timing?: {           // Quote timing information
    createdAt: number;
    expiresAt: number;
    ttlMs: number;
    nextUpdateEstimate: number;
  };
  
  strikes: {
    higher: Strike[];
    lower: Strike[];
  };
  metadata?: {
    timeToExpiry: number;
    volatility: number;
    riskFreeRate: number;
    lastCalculated: number;
  };
}

// Message types for betting WebSocket
export interface InitialAssetData extends PriorityMessage {
  type: 'init_asset';
  data: {
    timestamp: number;
    assets: Asset[];
  };
}

// NEW: Atomic quote update message
export interface QuoteUpdate extends PriorityMessage {
  type: 'quote_update';
  data: {
    quoteId: string;
    symbol: string;
    currentPrice: number;
    confidence: number;
    strikes: {
      higher: Strike[];
      lower: Strike[];
    };
    timing: {
      createdAt: number;
      expiresAt: number;
      ttlMs: number;
      nextUpdateEstimate: number;
    };
    metadata: {
      timeToExpiry: number;
      volatility: number;
      riskFreeRate: number;
      calculatedAt: number;
    };
  };
}

// NEW: Price update message (high frequency)
export interface PriceUpdate extends PriorityMessage {
  type: 'price_update';
  data: Array<{
    symbol: string;
    price: number;
    confidence: number;
    timestamp: number;
  }>;
  timestamp: number;
}

// Deprecated interfaces removed - use QuoteUpdate only

export interface BettingError extends PriorityMessage {
  type: 'betting_error';
  data: {
    errorType: string;
    message: string;
    timestamp: number;
  };
}

interface LivenessPing extends BaseMessage {
  type: 'liveness_ping';
  pingId: string;
  timestamp: number;
}

// Refresh completion messages
interface RefreshCompleted extends BaseMessage {
  type: 'refresh_completed';
  data?: {
    message: string;
  };
}

interface RefreshError extends BaseMessage {
  type: 'refresh_error';
  data: {
    message: string;
  };
}

// Subscription confirmation message
interface SubscriptionConfirmed extends BaseMessage {
  type: 'subscription_confirmed';
  message: string;
}

// Settlement message (following quote pattern)
export interface BetSettled extends BaseMessage {
  type: 'bet_settled';
  data: {
    betId: string;
    userWallet: string;
    status: 'won' | 'lost';
    finalResults: any[];
    actualPayout: number;
    settledAt: string;
    timestamp: number;
  };
}

// NEW: Bet placement message for social features
export interface BetPlaced extends BaseMessage {
  type: 'bet_placed';
  data: {
    betId: string;
    userWallet: string; // Anonymized (first 8 + last 4 chars)
    betAmount: number;
    potentialWin: number;
    raceSelections: {
      raceId: string;
      selectedAssets: string[];
      betType: string;
      odds: number;
    }[];
    roundId: string;
    placedAt: string;
    timestamp: number;
  };
}

// Signed race details from backend (for on-chain betting)
export interface SignedRaceDetails {
  round_id: number;
  start_timestamp: number;
  end_timestamp: number;
  cars_with_odds: Array<{
    car: string;
    odds: number; // x1000 format (e.g., 1800 = 1.8x)
  }>;
  signature: number[]; // 64-byte Ed25519 signature
}

// Signed race results from backend (for on-chain settlement)
export interface SignedResults {
  round_id: number;
  results: string[]; // Winner array: [winner, loser]
  timestamp: number; // Unix seconds
  signature: number[]; // 64-byte Ed25519 signature
}

// NEW: Round-based messages from backend
export interface RoundUpdate extends PriorityMessage {
  type: 'round_update';
  data: {
    roundId: string;
    phase: 'BETTING' | 'RACING' | 'SETTLEMENT' | 'RESULTS';
    status: string;
    raceStartTimestamp?: number; // Unix seconds from backend
    raceEndTimestamp?: number; // Unix seconds from backend
    totalBets: number;
    timestamp: number;
    
    // Phase-specific data
    raceOdds?: {
      races: { [raceId: string]: BackendRaceData };
      recentPerformance: { [symbol: string]: RecentPerformanceData };
      raceWinners?: { [raceId: string]: string[] }; // NEW: Historical race winners
      duration: number;
      calculatedAt: number;
      expiresAt: number;
      metadata: {
        assetsAnalyzed: string[];
        dataWindow: string;
        houseEdge: number;
        raceConfigurations: number;
        totalRaces: number;
        updateInterval: string;
      };
    };
    
    // ✅ NEW: On-chain betting support
    signedRaceDetails?: SignedRaceDetails; // Available in BETTING, RACING, SETTLEMENT, RESULTS
    signedResults?: SignedResults; // Available in RESULTS phase
    
    // RACING phase data
    startingPrices?: { [symbol: string]: number };
    actualRaceStart?: string;
    
    // SETTLEMENT phase data
    settlementAttempts?: number;
    
    // RESULTS phase data
    endingPrices?: { [symbol: string]: number };
    results?: string[]; // Winner array: [winner, loser] for 1v1
    percentageGains?: { [symbol: string]: number }; // Final % gains for each asset
    settledBets?: number;
    actualRaceEnd?: string;
    settledAt?: string;
  };
}

// DEPRECATED: Legacy race odds messages - will be removed
export interface InitialRaceOddsMessage extends PriorityMessage {
  type: 'init_race_odds';
  data: {
    type: 'race_odds';
    format: 'multi_asset';
    races: { [raceId: string]: BackendRaceData };
    recentPerformance: { [symbol: string]: RecentPerformanceData };
    duration: number;
    calculatedAt: number;
    expiresAt: number;
    metadata: {
      assetsAnalyzed: string[];
      dataWindow: string;
      houseEdge: number;
      raceConfigurations: number;
      totalRaces: number;
      updateInterval: string;
    };
  };
}

export interface RaceOddsUpdateMessage extends PriorityMessage {
  type: 'race_odds_update';
  data: {
    type: 'race_odds';
    format: 'multi_asset';
    races: { [raceId: string]: BackendRaceData };
    recentPerformance: { [symbol: string]: RecentPerformanceData };
    duration: number;
    calculatedAt: number;
    expiresAt: number;
    metadata: {
      assetsAnalyzed: string[];
      dataWindow: string;
      houseEdge: number;
      raceConfigurations: number;
      totalRaces: number;
      updateInterval: string;
    };
  };
}

// Backend race data structure (actual from your data)
export interface BackendRaceData {
  id: string;
  assets: string[];
  raceType: '2-way' | '3-way';
  odds: { 
    [asset: string]: {
      symbol: string;
      winRate: number;
      multiplier: number;
      fairMultiplier: number;
      metadata: {
        confidence: string;
        raceType: string;
        ties: number;
        totalRaces: number;
        wins: number;
      };
    };
  };
  winRates: { [asset: string]: number };
  winCounts: { [asset: string]: number };
  ties: number;
  totalRaces: number;
}

// Recent performance data structure
export interface RecentPerformanceData {
  symbol: string;
  performance: number[]; // [5min ago, 4min ago, 3min ago, 2min ago, 1min ago]
  labels: string[];
  totalChange: number;
  trend: 'bullish' | 'bearish' | 'neutral';
}

// Airdrop message interfaces
export interface AirdropNewRound extends BaseMessage {
  type: 'airdrop:newRound';
  data: any;
}

export interface AirdropRoundUpdate extends BaseMessage {
  type: 'airdrop:roundUpdate';
  data: any;
}

export interface AirdropDistributionComplete extends BaseMessage {
  type: 'airdrop:distributionComplete';
  data: any;
}

// Game Session message interfaces
export interface GameSessionMessage extends BaseMessage {
  type: 'session_created' | 'asset_picked' | 'race_started' | 'settlement_started' | 'race_settled' | 'new_round' | 'session_ended' | 'session_expired' | 'round_expired' | 'round_reset';
  data: {
    sessionId: string;
    userWallet: string;
    sessionStatus: 'active' | 'cashed_out' | 'busted' | 'expired';
    roundsCompleted: number;
    currentMultiplier: number;
    initialBet: number;
    finalPayout?: number;
    round?: {
      roundId: string;
      roundNumber: number;
      phase: 'BETTING' | 'RACING' | 'SETTLEMENT' | 'RESULTS';
      champion: string;
      challenger: string;
      playerPick?: string;
      bettingEndTime?: string;
      raceStartTime?: string;
      raceEndTime?: string;
      startingPrices?: { [symbol: string]: number };
      endingPrices?: { [symbol: string]: number };
      raceWinner?: string;
      playerWon?: boolean;
      playerDecision?: string;
      signedRaceDetails?: {
        round_id: number;
        start_timestamp: number;
        end_timestamp: number;
        cars_with_odds: Array<{
          car: string;
          odds: number;
        }>;
        signature: number[];
      };
      signedResults?: {
        round_id: number;
        results: string[];
        timestamp: number;
        signature: number[];
      };
    };
    timestamp: number;
  };
}

// Wallet authentication messages
export interface AuthSuccessMessage extends BaseMessage {
  type: 'auth_success';
  message: string;
  walletAddress: string;
}

export interface AuthErrorMessage extends BaseMessage {
  type: 'auth_error';
  message: string;
}

export interface WalletRegisteredMessage extends BaseMessage {
  type: 'wallet_registered';
  message: string;
  walletAddress: string;
}

export interface RegistrationErrorMessage extends BaseMessage {
  type: 'registration_error';
  message: string;
}

type BettingMessage = InitialAssetData | QuoteUpdate | PriceUpdate | BettingError | LivenessPing | RefreshCompleted | RefreshError | SubscriptionConfirmed | BetSettled | BetPlaced | RoundUpdate | InitialRaceOddsMessage | RaceOddsUpdateMessage | AirdropNewRound | AirdropRoundUpdate | AirdropDistributionComplete | GameSessionMessage | AuthSuccessMessage | AuthErrorMessage | WalletRegisteredMessage | RegistrationErrorMessage;

// Callback types
export type InitialAssetDataCallback = (update: InitialAssetData) => void;
export type QuoteUpdateCallback = (update: QuoteUpdate) => void;
export type PriceUpdateCallback = (update: PriceUpdate) => void;
export type BettingErrorCallback = (error: BettingError) => void;
export type BetSettledCallback = (update: BetSettled) => void;
export type BetPlacedCallback = (update: BetPlaced) => void;
export type RoundUpdateCallback = (update: RoundUpdate) => void;
export type InitialRaceOddsCallback = (update: InitialRaceOddsMessage) => void;
export type RaceOddsUpdateCallback = (update: RaceOddsUpdateMessage) => void;
export type GameSessionCallback = (update: GameSessionMessage) => void;

class BettingWebSocketService {
  private ws: WebSocket | null = null;
  private initialAssetCallbacks: Set<InitialAssetDataCallback> = new Set();
  private quoteCallbacks: Set<QuoteUpdateCallback> = new Set(); // Atomic quote callbacks
  private priceUpdateCallbacks: Set<PriceUpdateCallback> = new Set(); // Price update callbacks
  private errorCallbacks: Set<BettingErrorCallback> = new Set();
  private settlementCallbacks: Set<BetSettledCallback> = new Set(); // Settlement callbacks
  private betPlacedCallbacks: Set<BetPlacedCallback> = new Set(); // NEW: Bet placement callbacks
  
  // NEW: Round update callbacks
  private roundUpdateCallbacks: Set<RoundUpdateCallback> = new Set();
  
  // NEW: Game session callbacks
  private gameSessionCallbacks: Set<GameSessionCallback> = new Set();
  
  // Airdrop callbacks
  private airdropCallbacks: Set<(event: { type: string; data: any }) => void> = new Set();
  
  // DEPRECATED: Legacy race odds callbacks
  private initialRaceOddsCallbacks: Set<InitialRaceOddsCallback> = new Set();
  private raceOddsUpdateCallbacks: Set<RaceOddsUpdateCallback> = new Set();
  
  private callbacks: Set<(message: any) => void> = new Set(); // Generic callbacks for other message types
  
  // Connection management
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 8;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000;
  
  // Connection health monitoring
  private lastAssetUpdate = 0;
  private connectionHealthTimer: NodeJS.Timeout | null = null;
  
  // Page visibility tracking - betting-friendly timeouts
  private hiddenTimeoutBetting = 5 * 60 * 1000; // 5 minutes during active betting
  private hiddenTimeoutWaiting = 15 * 60 * 1000; // 15 minutes during waiting periods
  private hiddenTimeoutId: NodeJS.Timeout | null = null;
  private isPageHidden = false;
  private wasDisconnectedByIdle = false;
  private visibilityListenersSetup = false;
  
  // Wallet authentication for user-specific routing
  private walletAddress: string | null = null;
  private isAuthenticated = false;

  constructor(private backendUrl: string = getWebSocketUrl()) {
    // Singleton pattern
    if (typeof window !== 'undefined' && window.__bettingWebSocketService && window.__bettingWebSocketService !== this) {
      return window.__bettingWebSocketService;
    }
    
    if (typeof window !== 'undefined') {
      window.__bettingWebSocketService = this;
    }
    
    this.connect();
    this.setupPageVisibilityTracking();
    this.startConnectionHealthMonitoring();
  }

  private connect(): void {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    this.isConnecting = true;

    try {
      this.ws = new WebSocket(`${this.backendUrl}/api/betting-stream`);

      this.ws.onopen = () => {
        this.onReconnectionSuccess();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: BettingMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('❌ Error parsing betting WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('❌ Betting WebSocket error:', error);
        this.isConnecting = false;
      };

      this.ws.onclose = (event) => {
        this.isConnecting = false;
        
        // Only reconnect if not manually disconnected or page hidden
        if (!this.wasDisconnectedByIdle && event.code !== 1000) {
          this.scheduleReconnect();
        }
      };

    } catch (error) {
      console.error('❌ Error creating betting WebSocket connection:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  private sendSubscribe(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'subscribe' }));
    }
  }

  private handleMessage(message: BettingMessage): void {
    // Send acknowledgment for priority messages that require it
    if (message.requiresAck && message.messageId && message.sequence) {
      this.sendAcknowledgment(message.messageId, message.sequence);
    }
    
    switch (message.type) {
      case 'init_asset':
        // Track asset updates for health monitoring
        this.lastAssetUpdate = Date.now();
        
        this.initialAssetCallbacks.forEach(callback => {
          try {
            callback(message);
          } catch (error) {
            console.error('❌ Error in initial asset callback:', error);
          }
        });
        break;

      case 'quote_update':
        // NEW: Handle atomic quote updates
        // Track quote updates for health monitoring
        this.lastAssetUpdate = Date.now();
        
        this.quoteCallbacks.forEach(callback => {
          try {
            callback(message);
          } catch (error) {
            console.error('❌ Error in quote callback:', error);
          }
        });
        break;

      case 'price_update':
        // NEW: Handle price updates (high frequency)
        // Track price updates for health monitoring
        this.lastAssetUpdate = Date.now();
        
        this.priceUpdateCallbacks.forEach(callback => {
          try {
            callback(message);
          } catch (error) {
            console.error('❌ Error in price update callback:', error);
          }
        });
        break;

      case 'betting_error':
        console.error('🚨 Betting error from server:', message.data);
        
        this.errorCallbacks.forEach(callback => {
          try {
            callback(message);
          } catch (error) {
            console.error('❌ Error in error callback:', error);
          }
        });
        break;

      case 'bet_settled':
        // Handle bet settlement messages (following quote pattern)
        this.settlementCallbacks.forEach(callback => {
          try {
            callback(message);
          } catch (error) {
            console.error('❌ Error in settlement callback:', error);
          }
        });
        break;

      case 'bet_placed':
        // NEW: Handle bet placement messages for social features
        this.betPlacedCallbacks.forEach(callback => {
          try {
            callback(message);
          } catch (error) {
            console.error('❌ Error in bet placed callback:', error);
          }
        });
        break;

      case 'round_update':
        
        // Track round updates for health monitoring
        this.lastAssetUpdate = Date.now();
        
        this.roundUpdateCallbacks.forEach(callback => {
          try {
            callback(message);
          } catch (error) {
            console.error('❌ Error in round update callback:', error);
          }
        });
        break;

      case 'session_created':
      case 'asset_picked':
      case 'race_started':
      case 'settlement_started':
      case 'race_settled':
      case 'new_round':
      case 'session_ended':
      case 'session_expired':
      case 'round_expired':
      case 'round_reset':
        // Handle game session messages
        this.gameSessionCallbacks.forEach(callback => {
          try {
            callback(message as GameSessionMessage);
          } catch (error) {
            console.error('❌ Error in game session callback:', error);
          }
        });
        break;

      case 'init_race_odds':
        // NEW: Handle initial race odds data from backend
        // Track race updates for health monitoring
        this.lastAssetUpdate = Date.now();
        
        this.initialRaceOddsCallbacks.forEach(callback => {
          try {
            callback(message);
          } catch (error) {
            console.error('❌ Error in initial race odds callback:', error);
          }
        });
        break;

      case 'race_odds_update':
        // Track race updates for health monitoring
        this.lastAssetUpdate = Date.now();
        
        this.raceOddsUpdateCallbacks.forEach(callback => {
          try {
            callback(message);
          } catch (error) {
            console.error('❌ Error in race odds update callback:', error);
          }
        });
        break;

      case 'liveness_ping':
        // Respond to server liveness check
        this.handleLivenessPing(message);
        break;

      case 'refresh_completed':
        // Handle successful refresh completion
        this.callbacks.forEach(callback => {
          callback({
            type: 'refresh_completed',
            data: (message as any).data || { message: 'Data refresh completed' }
          });
        });
        break;

      case 'refresh_error':
        // Handle refresh errors
        console.warn('❌ Betting data refresh failed:', (message as any).message);
        this.callbacks.forEach(callback => {
          callback({
            type: 'refresh_error', 
            data: { 
              errorType: 'refresh_failed',
              message: (message as any).message || 'Refresh failed'
            }
          });
        });
        break;
      
      case 'auth_success':
        // Handle successful wallet authentication
        this.isAuthenticated = true;
        break;
      
      case 'auth_error':
        // Handle authentication error
        this.isAuthenticated = false;
        console.error('❌ Wallet authentication failed:', (message as any).message);
        break;
      
      case 'wallet_registered':
        // Handle successful wallet registration confirmation
        break;
      
      case 'registration_error':
        // Handle wallet registration error
        break;

      case 'subscription_confirmed':
        // Handle subscription confirmation
        break;
        
      case 'airdrop:newRound':
        // Handle new airdrop round started
        this.airdropCallbacks.forEach(callback => {
          try {
            callback({ type: 'newRound', data: message.data });
          } catch (error) {
            console.error('❌ Error in airdrop callback:', error);
          }
        });
        break;
      
      case 'airdrop:roundUpdate':
        // Handle airdrop round updates (pool amount, eligible users count)
        this.airdropCallbacks.forEach(callback => {
          try {
            callback({ type: 'roundUpdate', data: message.data });
          } catch (error) {
            console.error('❌ Error in airdrop callback:', error);
          }
        });
        break;
      
      case 'airdrop:distributionComplete':
        // Handle airdrop distribution completion
        this.airdropCallbacks.forEach(callback => {
          try {
            callback({ type: 'distributionComplete', data: message.data });
          } catch (error) {
            console.error('❌ Error in airdrop callback:', error);
          }
        });
        break;
        
      default:
        // Handle any unknown message types (including deprecated ones)
        if ((message as any).type === 'strikes_update' || (message as any).type === 'price_update') {
          console.warn(`⚠️ Received deprecated ${(message as any).type} message - backend should use quote_update only`);
        } else {
          console.warn('⚠️ Unknown betting message type:', (message as any).type);
        }
    }
  }

  private sendAcknowledgment(messageId: string, sequence: number): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const ack = {
        type: 'ack',
        messageId: messageId,
        sequence: sequence,
        timestamp: Date.now()
      };
      
      try {
        this.ws.send(JSON.stringify(ack));
      } catch (error) {
        console.error('❌ Failed to send betting acknowledgment:', error);
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('🚨 Max reconnection attempts reached for betting WebSocket');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );
    
    setTimeout(() => {
      if (!this.isPageHidden) {
        this.connect();
      }
    }, delay);
  }

  private setupPageVisibilityTracking(): void {
    if (this.visibilityListenersSetup || typeof document === 'undefined') {
      return;
    }

    this.visibilityListenersSetup = true;

    const handleVisibilityChange = () => {
      const isHidden = document.hidden;

      if (isHidden && !this.isPageHidden) {
        // Page became hidden
        this.isPageHidden = true;
        
        // Use betting timeout (shorter than round timeout since betting is more active)
        const timeout = this.hiddenTimeoutBetting;
                
        this.hiddenTimeoutId = setTimeout(() => {
          if (this.isPageHidden && this.ws) {
            this.wasDisconnectedByIdle = true;
            this.ws.close(1000, 'Page hidden timeout');
          }
        }, timeout);

      } else if (!isHidden && this.isPageHidden) {
        // Page became visible
        this.isPageHidden = false;
        
        if (this.hiddenTimeoutId) {
          clearTimeout(this.hiddenTimeoutId);
          this.hiddenTimeoutId = null;
        }

        // Reconnect if we were disconnected due to idle
        if (this.wasDisconnectedByIdle) {
          this.wasDisconnectedByIdle = false;
          this.connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  // Public API for BettingProvider
  public addInitialAssetCallback(callback: InitialAssetDataCallback): void {
    this.initialAssetCallbacks.add(callback);
  }

  public removeInitialAssetCallback(callback: InitialAssetDataCallback): void {
    this.initialAssetCallbacks.delete(callback);
  }

  // NEW: Quote callback methods for atomic updates
  public addQuoteCallback(callback: QuoteUpdateCallback): void {
    this.quoteCallbacks.add(callback);
  }

  public removeQuoteCallback(callback: QuoteUpdateCallback): void {
    this.quoteCallbacks.delete(callback);
  }

  // NEW: Price update callback methods (high frequency)
  public addPriceUpdateCallback(callback: PriceUpdateCallback): void {
    this.priceUpdateCallbacks.add(callback);
  }

  public removePriceUpdateCallback(callback: PriceUpdateCallback): void {
    this.priceUpdateCallbacks.delete(callback);
  }

  // Settlement callback methods (following quote pattern)
  public addSettlementCallback(callback: BetSettledCallback): void {
    this.settlementCallbacks.add(callback);
  }

  public removeSettlementCallback(callback: BetSettledCallback): void {
    this.settlementCallbacks.delete(callback);
  }

  // NEW: Bet placement callback methods for social features
  public addBetPlacedCallback(callback: BetPlacedCallback): void {
    this.betPlacedCallbacks.add(callback);
  }

  public removeBetPlacedCallback(callback: BetPlacedCallback): void {
    this.betPlacedCallbacks.delete(callback);
  }

  // NEW: Round update callback methods
  public addRoundUpdateCallback(callback: RoundUpdateCallback): void {
    this.roundUpdateCallbacks.add(callback);
  }

  public removeRoundUpdateCallback(callback: RoundUpdateCallback): void {
    this.roundUpdateCallbacks.delete(callback);
  }

  public addGameSessionCallback(callback: GameSessionCallback): void {
    this.gameSessionCallbacks.add(callback);
  }

  public removeGameSessionCallback(callback: GameSessionCallback): void {
    this.gameSessionCallbacks.delete(callback);
  }

  // NEW: Race odds callback methods
  public addInitialRaceOddsCallback(callback: InitialRaceOddsCallback): void {
    this.initialRaceOddsCallbacks.add(callback);
  }

  public removeInitialRaceOddsCallback(callback: InitialRaceOddsCallback): void {
    this.initialRaceOddsCallbacks.delete(callback);
  }

  public addRaceOddsUpdateCallback(callback: RaceOddsUpdateCallback): void {
    this.raceOddsUpdateCallbacks.add(callback);
  }

  public removeRaceOddsUpdateCallback(callback: RaceOddsUpdateCallback): void {
    this.raceOddsUpdateCallbacks.delete(callback);
  }

  // DEPRECATED: Legacy callback methods removed - use addQuoteCallback() instead

  public addErrorCallback(callback: BettingErrorCallback): void {
    this.errorCallbacks.add(callback);
  }

  public removeErrorCallback(callback: BettingErrorCallback): void {
    this.errorCallbacks.delete(callback);
  }

  // Airdrop callback methods
  public addAirdropCallback(callback: (event: { type: string; data: any }) => void): void {
    this.airdropCallbacks.add(callback);
  }

  public removeAirdropCallback(callback: (event: { type: string; data: any }) => void): void {
    this.airdropCallbacks.delete(callback);
  }

  // Generic callback methods for other message types (like refresh_completed)
  public addCallback(callback: (message: any) => void): void {
    this.callbacks.add(callback);
  }

  public removeCallback(callback: (message: any) => void): void {
    this.callbacks.delete(callback);
  }

  public requestRefresh(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'request_refresh' }));
    }
  }

  public getConnectionState(): 'connecting' | 'connected' | 'disconnected' {
    if (this.isConnecting) return 'connecting';
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return 'connected';
    return 'disconnected';
  }

  public disconnect(): void {
    if (this.hiddenTimeoutId) {
      clearTimeout(this.hiddenTimeoutId);
      this.hiddenTimeoutId = null;
    }
    
    if (this.connectionHealthTimer) {
      clearInterval(this.connectionHealthTimer);
      this.connectionHealthTimer = null;
    }
    
    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }
  }

  /**
   * Start monitoring connection health
   */
  private startConnectionHealthMonitoring(): void {
    if (this.connectionHealthTimer) {
      clearInterval(this.connectionHealthTimer);
    }
    
    // Initialize timestamp to prevent false alarms on startup
    this.lastAssetUpdate = Date.now();
    
    this.connectionHealthTimer = setInterval(() => {
      this.checkConnectionHealth();
    }, 10000); // Check every 10 seconds
  }

  /**
   * Check if connection is healthy based on expected update frequency
   */
  private checkConnectionHealth(): void {
    const now = Date.now();
    const timeSinceLastUpdate = now - this.lastAssetUpdate;
    
    // Expect asset updates at least every 30 seconds during normal operation
    if (timeSinceLastUpdate > 30000 && this.ws?.readyState === WebSocket.OPEN) {
      console.warn('⚠️ No betting asset updates received in 30s, connection may be stale');
      
      // Send ping to check if connection is alive
      try {
        this.ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
      } catch (error) {
        console.error('❌ Failed to send betting ping:', error);
      }
    }
  }

  /**
   * Handle liveness ping from server
   */
  private handleLivenessPing(pingMessage: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const pong = {
        type: 'liveness_pong',
        pingId: pingMessage.pingId,
        originalTimestamp: pingMessage.timestamp,
        timestamp: Date.now()
      };
      
      try {
        this.ws.send(JSON.stringify(pong));
      } catch (error) {
        console.error('❌ Failed to send betting liveness pong:', error);
      }
    }
  }

  /**
   * Enhanced reconnection success handling
   */
  private async onReconnectionSuccess(): Promise<void> {
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    
    // Reset health monitoring timers
    this.lastAssetUpdate = Date.now();
    
    // Notify UI of successful reconnection
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('bettingConnectionRestored', {
        detail: { type: 'success' }
      }));
    }
    
    // Register wallet for user-specific routing
    if (this.walletAddress) {
      this.registerWalletWithServer(this.walletAddress);
    }
    
    // Send subscription message to backend
    this.sendSubscribe();
  }
  
  /**
   * Set wallet address for user-specific message routing
   * Call this when wallet connects/changes
   */
  setWalletAddress(walletAddress: string | null): void {
    if (this.walletAddress === walletAddress) {
      return; // No change
    }
    
    this.walletAddress = walletAddress;
    this.isAuthenticated = false;
    
    // Register wallet immediately if connected
    if (walletAddress && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.registerWalletWithServer(walletAddress);
    }
  }
  
  /**
   * Register wallet address with backend for user-specific routing
   * Note: This is NOT authentication - just tells the server which wallet owns this connection
   */
  private registerWalletWithServer(walletAddress: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    
    try {
      this.ws.send(JSON.stringify({
        type: 'register_wallet',
        walletAddress: walletAddress
      }));
    } catch (error) {
      console.error('❌ Failed to register wallet:', error);
    }
  }
  
  /**
   * Get current wallet address
   */
  getWalletAddress(): string | null {
    return this.walletAddress;
  }
  
  /**
   * Check if wallet is authenticated
   */
  isWalletAuthenticated(): boolean {
    return this.isAuthenticated;
  }
}

// Create singleton instance
export const bettingWebSocketService = new BettingWebSocketService();

// Add to window for singleton pattern
declare global {
  interface Window {
    __bettingWebSocketService?: BettingWebSocketService;
  }
}
